import clsx from "clsx";
import {
  controlClass,
  describedBy,
  FieldError,
  HelpText,
  isRequiredNow,
  labelClass,
  RequiredMark,
  wrapperProps,
  type FieldProps,
} from "./shared";

/**
 * The port of fragments/forms/fields/textarea.php: a paragraph field with the
 * design's "N characters remaining" counter under it. The ceiling is enforced
 * by maxlength, so the count is only ever a read-out, and it counts down from
 * the full allowance as you type.
 */
export default function TextareaField(props: FieldProps) {
  const { id, name, field, value, error, shown, onChange, onFocus } = props;

  if (!id || !name || !field) {
    return null;
  }

  const required = isRequiredNow(field, shown);
  const maxLength = field.max_length || field.validation?.max_length || 0;
  const remaining = maxLength ? Math.max(0, maxLength - value.length) : 0;

  return (
    <div {...wrapperProps(props)}>
      <label className={labelClass} htmlFor={id}>
        {field.label} <RequiredMark field={field} />
      </label>

      <HelpText id={id} field={field} />

      <textarea
        className={clsx(
          controlClass,
          "resize-none js-field-el",
          maxLength && "js-field-counter-input",
        )}
        id={id}
        name={name}
        rows={5}
        value={value}
        maxLength={maxLength || undefined}
        data-counter-max={maxLength || undefined}
        placeholder={field.placeholder || undefined}
        readOnly={field.readonly || undefined}
        required={required}
        aria-required={required || undefined}
        aria-invalid={!!error}
        aria-describedby={describedBy(id, field)}
        data-validate="1"
        data-required={required ? "1" : undefined}
        onChange={(event) => onChange(name, event.target.value)}
        onFocus={() => onFocus(name)}
      />

      <div className="mt-1 flex items-center justify-between gap-3">
        <FieldError id={id} error={error} />

        {maxLength > 0 && (
          <p className="m-0 ml-auto text-[11px] leading-[1.4] text-[#9ca3af]">
            <span className="js-field-counter-remaining">{remaining}</span>
            {" characters remaining"}
          </p>
        )}
      </div>
    </div>
  );
}
